import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SiteFooter } from "../components/SiteFooter";
import { toast } from "sonner";
import {
  LogOut, Clock, CheckCircle, XCircle, AlertCircle,
  ChevronDown, ChevronUp, Calendar, MessageSquare, User,
  ExternalLink,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";

const STATUS_MAP = {
  new: { label: "Received", color: "#6B7280", icon: Clock, desc: "Your submission has been received and is under review." },
  booked: { label: "Consultation Booked", color: "#0F3221", icon: CheckCircle, desc: "Your consultation is booked. We'll reach out within 24 hours." },
  pending_approval: { label: "Pending Approval", color: "#D97706", icon: AlertCircle, desc: "Your application is being reviewed. We'll notify you within 48 hours." },
  approved: { label: "Approved", color: "#16A34A", icon: CheckCircle, desc: "You've been approved for direct cultivator access." },
  rejected: { label: "Not Approved", color: "#DC2626", icon: XCircle, desc: "This application was not approved at this time." },
  consulted: { label: "Consulted", color: "#6366F1", icon: CheckCircle, desc: "Your consultation is complete. Check your email for next steps." },
  deposit_paid: { label: "Deposit Received", color: "#0F3221", icon: CheckCircle, desc: "Your concierge deposit is confirmed. Trip planning is in progress." },
  complete: { label: "Complete", color: "#16A34A", icon: CheckCircle, desc: "Your experience is fully confirmed." },
};

const LANE_LABELS = {
  retail: "Retail Inquiry",
  budget: "Budget (Lows)",
  premium: "Premium (KookaMF)",
  boutique: "Boutique (Small Batch)",
  concierge: "Concierge",
};

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("submissions");
  const [submissions, setSubmissions] = useState([]);
  const [chats, setChats] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/me`, { withCredentials: true });
      setClient(res.data);
    } catch {
      navigate("/portal/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const fetchData = useCallback(async () => {
    if (!client) return;
    try {
      if (tab === "submissions") {
        const res = await axios.get(`${API}/client/submissions`, { withCredentials: true });
        setSubmissions(res.data);
      } else if (tab === "chats") {
        const res = await axios.get(`${API}/client/chats`, { withCredentials: true });
        setChats(res.data);
      }
    } catch (err) {
      if (err.response?.status === 401) navigate("/portal/login");
    }
  }, [client, tab, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/client/logout`, {}, { withCredentials: true });
    } catch { console.warn("Logout request failed"); }
    navigate("/portal/login");
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const formatTime = (d) => new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: "var(--accent)", borderTopColor: "transparent" }} />
          <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>Loading portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-primary)" }} data-testid="portal-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: "#0F3221", borderBottom: "1px solid rgba(244,241,237,0.1)" }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-8 logo-blend" /></a>
            <div className="hidden sm:block" style={{ borderLeft: "1px solid rgba(244,241,237,0.2)", paddingLeft: "12px" }}>
              <p className="text-xs font-semibold" style={{ color: "#F4F1ED" }}>Client Portal</p>
              <p className="text-[10px]" style={{ color: "rgba(244,241,237,0.5)" }}>{client?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5" style={{ background: "rgba(244,241,237,0.08)" }}>
              <User size={14} style={{ color: "rgba(244,241,237,0.6)" }} />
              <span className="text-xs font-medium" style={{ color: "#F4F1ED" }}>{client?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="portal-logout-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors"
              style={{ background: "none", border: "1px solid rgba(244,241,237,0.2)", color: "rgba(244,241,237,0.7)", cursor: "pointer" }}
            >
              <LogOut size={12} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-4xl mx-auto px-6 flex gap-0">
          {[
            { id: "submissions", label: "My Submissions", icon: Calendar },
            { id: "chats", label: "Chat History", icon: MessageSquare },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`portal-tab-${t.id}`}
              className="flex items-center gap-2 px-5 py-3.5 text-xs font-semibold transition-colors"
              style={{
                background: "none", border: "none", cursor: "pointer",
                borderBottom: tab === t.id ? "2px solid var(--accent)" : "2px solid transparent",
                color: tab === t.id ? "var(--text-primary)" : "var(--text-tertiary)",
              }}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Welcome Banner */}
        <div className="mb-8 p-6" style={{ background: "var(--primary)", border: "1px solid var(--primary)" }}>
          <p className="text-xs tracking-[0.2em] uppercase mb-1 font-semibold" style={{ color: "rgba(253,252,247,0.4)" }}>Welcome back</p>
          <h2 className="text-lg font-medium" style={{ color: "#F5F3E9", fontFamily: "'Playfair Display', serif" }}>{client?.name || "Client"}</h2>
          <p className="text-xs mt-1" style={{ color: "rgba(253,252,247,0.5)" }}>
            {submissions.length > 0 ? `You have ${submissions.length} submission${submissions.length !== 1 ? "s" : ""} on file.` : "Submit an intake form to get started."}
          </p>
        </div>
        {/* Submissions Tab */}
        {tab === "submissions" && (
          <div data-testid="portal-submissions-list">
            {submissions.length === 0 ? (
              <div className="text-center py-16" style={{ color: "var(--text-tertiary)" }}>
                <Calendar size={32} className="mx-auto mb-4 opacity-30" />
                <p className="text-sm mb-2">No submissions yet</p>
                <p className="text-xs">Submit an intake form to get started.</p>
                <Link to="/">
                  <Button className="mt-4 text-sm" style={{ background: "var(--accent)", color: "#FFFFFF" }}>
                    Start Intake Form
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-tertiary)" }}>
                  {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
                </p>
                {submissions.map((sub) => {
                  const status = STATUS_MAP[sub.status] || STATUS_MAP.new;
                  const StatusIcon = status.icon;
                  const isExpanded = expandedId === sub.id;

                  return (
                    <div key={sub.id} style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }} data-testid={`portal-submission-${sub.id}`}>
                      <div
                        className="p-5 cursor-pointer flex items-center justify-between"
                        onClick={() => setExpandedId(isExpanded ? null : sub.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${status.color}10`, border: `1px solid ${status.color}30` }}>
                            <StatusIcon size={18} style={{ color: status.color }} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{status.label}</p>
                            <p className="text-xs mt-0.5" style={{ color: "var(--text-tertiary)" }}>
                              {LANE_LABELS[sub.lane] || sub.lane} &middot; {formatDate(sub.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {sub.calendly_url && (
                            <a
                              href={sub.calendly_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`portal-book-btn-${sub.id}`}
                              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors"
                              style={{ background: "var(--accent)", color: "#FFFFFF" }}
                            >
                              <Calendar size={12} /> Book Session
                            </a>
                          )}
                          {isExpanded ? <ChevronUp size={16} style={{ color: "var(--text-tertiary)" }} /> : <ChevronDown size={16} style={{ color: "var(--text-tertiary)" }} />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-5 pb-5" style={{ borderTop: "1px solid var(--border)" }}>
                          <p className="text-sm mt-4 mb-4" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>{status.desc}</p>

                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="p-3" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)" }}>
                              <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Budget</p>
                              <p className="text-sm font-medium mt-1" style={{ color: "var(--text-primary)" }}>{sub.budget?.replace(/_/g, " ") || "---"}</p>
                            </div>
                            <div className="p-3" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)" }}>
                              <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Payment</p>
                              <p className="text-sm font-medium mt-1" style={{ color: sub.payment_status === "paid" ? "#16A34A" : "var(--text-primary)" }}>
                                {sub.payment_status === "paid" ? `Paid -- $${sub.paid_amount || 0}` : "Pending"}
                              </p>
                            </div>
                          </div>

                          {sub.tags && sub.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {sub.tags.map((tag) => (
                                <span key={tag} className="text-xs px-2 py-1" style={{ background: "rgba(15,50,33,0.05)", border: "1px solid rgba(15,50,33,0.12)", color: "var(--primary)" }}>{tag}</span>
                              ))}
                            </div>
                          )}

                          {/* Calendly booking — mobile visible */}
                          {sub.calendly_url && (
                            <a
                              href={sub.calendly_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`portal-book-expanded-${sub.id}`}
                              className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold mb-4 transition-colors"
                              style={{ background: "var(--accent)", color: "#FFFFFF" }}
                            >
                              <Calendar size={14} /> Book Your Session <ExternalLink size={12} />
                            </a>
                          )}

                          <p className="text-[10px]" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>
                            Ref: {sub.id}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Chat History Tab */}
        {tab === "chats" && (
          <div data-testid="portal-chats-list">
            {chats.length === 0 ? (
              <div className="text-center py-16" style={{ color: "var(--text-tertiary)" }}>
                <MessageSquare size={32} className="mx-auto mb-4 opacity-30" />
                <p className="text-sm mb-2">No chat history</p>
                <p className="text-xs">Use the chat widget to start a conversation with our team.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-tertiary)" }}>
                  {chats.length} conversation{chats.length !== 1 ? "s" : ""}
                </p>
                {chats.map((chat) => (
                  <div key={chat.session_id} style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
                    <div className="p-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
                      <div className="flex items-center gap-3">
                        <MessageSquare size={16} style={{ color: "var(--accent)" }} />
                        <div>
                          <p className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>
                            {chat.messages?.length || 0} messages
                          </p>
                          <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>{formatDate(chat.created_at)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                      {(chat.messages || []).map((msg, i) => (
                        <div key={`${msg.role}-${msg.timestamp || i}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div
                            className="max-w-[80%] px-3 py-2 text-xs"
                            style={{
                              background: msg.role === "user" ? "var(--accent)" : "var(--bg-tertiary)",
                              color: msg.role === "user" ? "#FFFFFF" : "var(--text-primary)",
                              borderRadius: msg.role === "user" ? "12px 2px 12px 12px" : "2px 12px 12px 12px",
                              lineHeight: 1.5,
                            }}
                          >
                            {msg.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
